import ModalCloseButton from '../../../../Component/UI/ModalCloseButton';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import { SellerBankAccount } from '../../../../Component/Interface/Store';

type Candidate = { order_seq: number; live_seq: number; live_title?: string; order_name: string; bank_due_amount: number; pg_due_amount: number };
type DepositRow = {
  row: number; order_seq?: number; amount: number | null; depositor: string;
  status: 'paid' | 'review' | 'duplicate' | 'error'; message?: string; candidates?: Candidate[];
  review_reason?: 'duplicate_match';
};
const messageFor = (error: any, fallback: string) => typeof error?.response?.data?.message === 'string' ? error.response.data.message : fallback;
const validResult = (data: any) => Array.isArray(data?.rows)
  && data.rows.every((row: DepositRow) => ['paid', 'review', 'duplicate', 'error'].includes(row.status)
    && (row.review_reason === undefined || row.review_reason === 'duplicate_match'))
  && [data.total, data.success, data.duplicate, data.fail].every(value => Number.isInteger(value) && value >= 0);
const statusLabel = { paid: '입금 처리 완료', review: '검토 필요', duplicate: '이미 처리됨', error: '처리 불가' };
const resultLabel = (row: DepositRow) => row.status === 'review' && row.review_reason === 'duplicate_match' ? '중복' : statusLabel[row.status];

export default function DepositExcelImport({ liveSeq, onConfirmed, disabled = false }: {
  liveSeq: number; onConfirmed: () => Promise<unknown> | void; disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accounts, setAccounts] = useState<SellerBankAccount[]>([]);
  const [accountError, setAccountError] = useState('');
  const [accountSeq, setAccountSeq] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<DepositRow[]>([]);
  const [choices, setChoices] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const hasResult = rows.length > 0 || !!message;
  const [dragging, setDragging] = useState(false);
  const operation = useRef(false);
  const request = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const account = accounts.find(item => String(item.seq) === accountSeq);
  useEffect(() => {
    setRows([]); setChoices({}); setFile(null); setError(''); setMessage(''); setBusy(false); operation.current = false;
    return () => { request.current += 1; };
  }, [liveSeq]);
  const loadAccounts = async () => {
    const current = ++request.current; setAccountLoading(true); setAccountError('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}store/bank-accounts`, { params: { purpose: 'orders' } });
      if (current !== request.current) return;
      if (!Array.isArray(data?.accounts)) throw new Error('Invalid accounts');
      setAccounts(data.accounts);
    } catch (cause) { if (current === request.current) setAccountError(messageFor(cause, '입금 계좌를 불러오지 못했습니다. 다시 시도해주세요.')); }
    finally { if (current === request.current) setAccountLoading(false); }
  };
  useEffect(() => { if (open) loadAccounts(); }, [open, liveSeq]);
  const closeModal = useCallback(() => {
    if (operation.current) return;
    request.current += 1;
    setDragging(false);
    setOpen(false);
    trigger.current?.focus();
  }, []);
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    return () => { document.body.style.overflow = previousOverflow; };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !hasResult && !operation.current) closeModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => { document.removeEventListener('keydown', onKeyDown); };
  }, [open, closeModal, hasResult]);
  const formFor = (targetFile: File) => {
    const form = new FormData(); form.append('file', targetFile); form.append('bank_account_seq', accountSeq); form.append('live_seq', String(liveSeq));
    return form;
  };
  const processFile = async (targetFile?: File) => {
    if (!targetFile || operation.current || disabled || !account?.excel_mapping) return;
    if (!/\.xlsx?$/i.test(targetFile.name) || targetFile.size > 5 * 1024 * 1024) { setError('5MB 이하의 .xlsx 또는 .xls 파일을 선택해주세요.'); return; }
    const current = ++request.current;
    operation.current = true; setBusy(true); setError(''); setMessage(''); setRows([]); setChoices({}); setFile(targetFile);
    try {
      const { data } = await api.post(`${SERVER_ADDRESS}orders/deposit-excel-import`, formFor(targetFile), { headers: { 'Content-Type': 'multipart/form-data' } });
      if (current !== request.current) return;
      if (!validResult(data)) throw new Error('Invalid import');
      setRows(data.rows);
      setMessage(data.total ? `자동 입금 처리 ${data.success}건 · 이미 처리된 거래 ${data.duplicate}건 · 검토 및 오류 ${data.fail}건` : '파일에 확인할 입금 내역이 없습니다.');
      if (data.success > 0) await onConfirmed();
    } catch (cause) { if (current === request.current) setError(messageFor(cause, '입금 처리 결과를 확인하지 못했습니다. 주문 내역을 새로 조회한 뒤 같은 파일로 다시 시도해주세요.')); }
    finally { if (current === request.current) { operation.current = false; setBusy(false); } }
  };
  const importFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const targetFile = event.target.files?.[0]; event.target.value = '';
    void processFile(targetFile);
  };
  const dropFile = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled || busy || accountLoading || !!accountError || !liveSeq || !account?.excel_mapping) return;
    void processFile(event.dataTransfer.files?.[0]);
  };
  const resolveRow = async (row: DepositRow) => {
    const orderSeq = Number(choices[row.row]);
    if (!file || !account || !orderSeq || operation.current || disabled) return;
    const current = ++request.current;
    operation.current = true; setBusy(true); setError(''); setMessage('');
    try {
      const form = formFor(file); form.append('row', String(row.row)); form.append('order_seq', String(orderSeq)); form.append('confirmed', 'true');
      const { data } = await api.post(`${SERVER_ADDRESS}orders/deposit-excel-resolve`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (current !== request.current) return;
      if (!validResult(data) || data.rows.length !== 1 || data.rows[0].row !== row.row) throw new Error('Invalid resolution');
      setRows(previous => previous.map(item => item.row === row.row ? data.rows[0] : item));
      setMessage(`${row.row}행: ${data.rows[0].message || resultLabel(data.rows[0])}`);
      if (data.success > 0) await onConfirmed();
    } catch (cause) { if (current === request.current) setError(messageFor(cause, '이 주문의 입금 확인을 반영하지 못했습니다. 주문 내역을 확인하고 다시 시도해주세요.')); }
    finally { if (current === request.current) { operation.current = false; setBusy(false); } }
  };
  return <>
    <Trigger ref={trigger} type="button" aria-haspopup="dialog" aria-expanded={open} disabled={busy} onClick={() => setOpen(true)}>무통장 입금 엑셀 확인</Trigger>
    {open && createPortal(<Overlay onMouseDown={event => { if (!hasResult && event.target === event.currentTarget) closeModal(); }}>
      <Dialog role="dialog" aria-modal="true" aria-labelledby="deposit-excel-title">
        <ModalHeader><h2 id="deposit-excel-title">무통장 입금 엑셀 확인</h2>
          <CloseButton tone="admin" ref={closeButton} type="button" aria-label="닫기" disabled={busy} onClick={closeModal} />
        </ModalHeader>
        <ModalBody>
      <p>입금 계좌를 선택하고 해당 은행의 엑셀을 올려주세요. 입금자명과 입금액이 주문 한 건에만 일치하면 바로 입금 처리됩니다. 여러 주문이 일치하거나 이전 처리 내역과 이름·금액이 같으면 직접 확인합니다.</p>
      {accountLoading && <p role="status">입금 계좌를 불러오는 중입니다.</p>}
      {accountError && <p role="alert">{accountError} <button type="button" disabled={busy} onClick={loadAccounts}>입금 계좌 다시 불러오기</button></p>}
      <label className="account">입금 내역의 계좌<select aria-label="입금 내역의 계좌" value={accountSeq} disabled={disabled || busy || accountLoading || !!accountError} onChange={event => {
        setAccountSeq(event.target.value); setRows([]); setChoices({}); setFile(null); setMessage(''); setError('');
      }}><option value="">입금 계좌를 선택해주세요</option>{accounts.map(item => <option key={item.seq} value={item.seq}>{item.label} · {item.bank_name} {item.bank_num}{item.active ? '' : ' (사용 중지)'}</option>)}</select></label>
      {!accountLoading && !accountError && !accounts.length && <p>등록된 입금 계좌가 없습니다. <a href="/admin/setting">셀러 설정</a>에서 계좌와 은행 엑셀 양식을 등록해주세요.</p>}
      {account && !account.excel_mapping && <p>이 계좌에 연결한 엑셀 양식이 없습니다. <a href="/admin/setting">셀러 설정</a>에서 입금자명과 입금액 열을 지정해주세요.</p>}
      {account?.excel_mapping && <p>연결 양식: {account.excel_mapping.sheet_name} · 입금자명: {account.excel_mapping.depositor_column} · 입금액: {account.excel_mapping.amount_column}</p>}
      <DropZone role="group" aria-label="입금 엑셀 업로드" aria-disabled={disabled || busy || accountLoading || !!accountError || !account?.excel_mapping}
        $active={dragging} $disabled={disabled || busy || accountLoading || !!accountError || !account?.excel_mapping}
        onDragEnter={event => { event.preventDefault(); if (!disabled && !busy && !accountLoading && !accountError && account?.excel_mapping) setDragging(true); }}
        onDragOver={event => { event.preventDefault(); event.dataTransfer.dropEffect = disabled || busy || accountLoading || !!accountError || !account?.excel_mapping ? 'none' : 'copy'; }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false); }}
        onDrop={dropFile}>
        <strong>{account?.excel_mapping ? '엑셀 파일을 여기에 놓으면 자동으로 입금을 확인합니다.'
          : account ? '이 계좌의 엑셀 양식을 등록하면 파일을 놓을 수 있습니다.' : '입금 계좌를 선택하면 엑셀 파일을 놓을 수 있습니다.'}</strong>
        <span>.xlsx 또는 .xls · 최대 5MB · 최대 1,000행</span>
        <Actions><button type="button" disabled={disabled || busy || accountLoading || !!accountError || !liveSeq || !account?.excel_mapping} onClick={() => input.current?.click()}>{busy ? '입금 처리 중…' : '자동 매칭 및 입금 처리'}</button>
          <input ref={input} type="file" aria-label="무통장 입금 엑셀 파일" accept=".xlsx,.xls" onChange={importFile} disabled={disabled || busy || !account?.excel_mapping} hidden /></Actions>
      </DropZone>
      {file && <p>현재 파일: {file.name}</p>}
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      {rows.some(row => row.review_reason === 'duplicate_match') && <p role="status">중복으로 표시된 입금은 자동으로 처리하지 않습니다. 고객에게 확인한 뒤 실제 주문을 선택해주세요.</p>}
      {rows.length > 0 && <TableScroll><table><thead><tr><th>엑셀 행</th><th>입금자명</th><th>입금액</th><th>주문번호</th><th>처리 결과</th><th>검토 주문</th></tr></thead>
        <tbody>{rows.map(row => <tr key={row.row}><td>{row.row}</td><td>{row.depositor}</td><td>{typeof row.amount === 'number' && Number.isFinite(row.amount) ? `${row.amount.toLocaleString('ko-KR')}원` : '—'}</td><td>{row.order_seq || '—'}</td><td><strong>{resultLabel(row)}</strong>{row.message && <p>{row.message}</p>}</td><td>
          {row.status === 'review' && !!row.candidates?.length && <Review>
            <select aria-label={`${row.row}행 입금 확인 주문`} value={choices[row.row] || ''} disabled={disabled || busy} onChange={event => setChoices(previous => ({ ...previous, [row.row]: event.target.value }))}>
              <option value="">주문을 직접 선택해주세요</option>{row.candidates.map(candidate => <option key={candidate.order_seq} value={candidate.order_seq} disabled={candidate.live_seq !== liveSeq || candidate.pg_due_amount > 0}>
                주문 {candidate.order_seq} · {candidate.order_name} · {candidate.bank_due_amount.toLocaleString('ko-KR')}원{candidate.live_seq !== liveSeq ? ` · 다른 라이브 (${candidate.live_title || candidate.live_seq})` : ''}{candidate.pg_due_amount > 0 ? ' · PG 결제 대기' : ''}</option>)}</select>
            <p>실제로 새 입금이 들어온 주문인지 확인한 뒤 처리해주세요.</p>
            <button type="button" aria-label={`${row.row}행 이 주문 입금 확인`} disabled={disabled || busy || !choices[row.row]} onClick={() => resolveRow(row)}>이 주문 입금 확인</button>
          </Review>}
        </td></tr>)}</tbody></table></TableScroll>}
        </ModalBody>
      </Dialog>
    </Overlay>, document.body)}
  </>;
}

const Trigger = styled.button`
  min-height: 44px; padding: 10px 16px;
  border: 1px solid var(--admin-accent); border-radius: 8px; background: var(--admin-theme-surface, #fff);
  color: var(--admin-theme-link, var(--admin-accent-deep)); cursor: pointer; font: inherit; font-weight: 600;
  &:hover { background: var(--admin-theme-accent-soft, #f7f3ff); }
  &:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
`;
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1100; display: flex; align-items: center; justify-content: center;
  padding: 24px; box-sizing: border-box; background: rgba(30, 24, 40, .55);
  @media (max-width: 600px) { padding: 12px; }
`;
const Dialog = styled.section`
  display: flex; flex-direction: column; width: min(860px, 100%); max-height: calc(100dvh - 48px);
  overflow: hidden; border-radius: 16px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #302a39);
  box-shadow: 0 24px 70px rgba(26, 18, 42, .22);
  @media (max-width: 600px) { max-height: calc(100dvh - 24px); }
`;
const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 20px 24px; border-bottom: 1px solid var(--admin-theme-border, #e9e6ef);
  h2 { margin: 0; font-size: 21px; line-height: 1.4; }
  @media (max-width: 600px) { padding: 16px; h2 { font-size: 18px; } }
`;
const CloseButton = styled(ModalCloseButton)``;
const ModalBody = styled.div`
  overflow: auto; padding: 20px 24px 24px;
  p { margin: 10px 0; font-size: 14px; line-height: 1.6; }
  [role='alert'] { color: var(--admin-theme-danger, #b42318); }
  button { min-height: 44px; padding: 10px 14px; border: 1px solid var(--admin-accent); border-radius: 8px; background: var(--admin-theme-surface, white); color: var(--admin-theme-link, var(--admin-accent-deep)); cursor: pointer; font: inherit; }
  button:disabled { cursor: not-allowed; opacity: .6; }
  button:focus-visible, select:focus-visible { outline: 3px solid var(--admin-theme-focus, #a993ef); outline-offset: 2px; }
  .account { display: flex; flex-direction: column; align-items: flex-start; gap: 8px; margin: 16px 0; font-weight: 600; }
  .account select { width: 100%; font-weight: 400; }
  select { box-sizing: border-box; max-width: 100%; min-height: 44px; padding: 8px 12px; border: 1px solid var(--admin-theme-border, #b8b3c0); border-radius: 8px; background: var(--admin-theme-surface, white); font: inherit; }
  @media (max-width: 600px) { padding: 16px; }
`;
const Actions = styled.div`display: flex; gap: 12px; flex-wrap: wrap; margin-top: 12px;`;
const DropZone = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  margin-top: 20px; padding: 28px 16px; border: 2px dashed ${props => props.$active ? '#603bd5' : 'var(--admin-theme-border, #cfc3ee)'};
  border-radius: 12px; background: ${props => props.$active ? 'var(--admin-theme-accent-soft, #eee7ff)' : 'var(--admin-theme-raised, #faf8ff)'};
  text-align: center; opacity: ${props => props.$disabled ? .6 : 1};
  strong { font-size: 15px; }
  span { color: var(--admin-theme-muted, #746d7d); font-size: 13px; }
  ${Actions} { margin-top: 8px; }
`;
const Review = styled.div`min-width: 250px; max-width: 420px; select { width: 100%; }`;
const TableScroll = styled.div`
  overflow: auto; max-height: 500px;
  table { border-collapse: collapse; width: 100%; min-width: 820px; text-align: left; font-size: 14px; }
  th, td { padding: 12px; border-bottom: 1px solid var(--admin-theme-border, #e9e6ef); overflow-wrap: anywhere; vertical-align: top; }
  th { background: var(--admin-theme-raised, #f7f5fa); }
`;
